import React from 'react';
import { FORM_STEP_CLASS, SURFACE_CONTENT_CLASS, getPageTransitionClasses } from '../Util/pageTransition';
import { DRAWER_FORM_MIN_HEIGHT, formCardClass } from '../Util/formHeightUtils';
import { useStepFocus } from '../hooks/useStepFocus';
import FormTitleStrip from './FormTitleStrip';

function FormStartPageShell({
  page,
  pageName = 'START_PAGE',
  direction = null,
  /** Event forms take the event page floor (formCardClass). */
  isEvent = false,
  isDrawerOpen,
  isHostedPage,
  formDisplayMode,
  fixedFormElementHeight,
  fixedFormElementStyles,
  fixedFormBodyStyles,
  containerId,
  headerId,
  bodyId,
  headerTitle,
  onHeaderBack,
  headerBackId,
  drawerBodyClassName,
  nonDrawerBodyClassName,
  bodyClassName = '',
  children,
  faq = null,
  usePageContainer = true,
}) {
  const isOverlay = formDisplayMode === 'overlay';
  const wrapperClassName = `${isHostedPage && !isDrawerOpen ? 'hosted-spacing-wrapper' : !isHostedPage && !isOverlay && !isDrawerOpen ? 'dynamic-spacing-wrapper' : ''} ${isOverlay ? 'flex-1 flex flex-col' : ''}`;
  // The card, with the page floor in the embed and the hosted page (formCardClass).
  const innerClassName = formCardClass({ isDrawerOpen, isHostedPage, isOverlay, isEvent });
  const resolvedNonDrawerBodyClassName = nonDrawerBodyClassName ?? '';
  const shouldStretchTransitionWrapper = Boolean(
    fixedFormElementHeight || fixedFormElementStyles?.height || isDrawerOpen || isOverlay,
  );
  const pageTransitionClassName = getPageTransitionClasses(page === pageName, direction, formDisplayMode);
  const stepRef = useStepFocus(page === pageName);
  const pageMarkup = (
    <div
      ref={stepRef}
      className={`page ${FORM_STEP_CLASS} ${page === pageName ? 'active' : ''} ${!isDrawerOpen && !isHostedPage && !isOverlay ? 'container' : ''} ${!isDrawerOpen && !isOverlay ? 'mx-auto' : ''} h-full items-center ${isDrawerOpen ? 'w-full px-1' : isHostedPage ? 'max-w-lg px-3' : isOverlay ? 'w-full' : 'max-w-md'} iframe-content-container page-transition-container`.trim()}
      id={containerId}
    >
      <div
        className={wrapperClassName}
        style={{
          ...(!isHostedPage || isDrawerOpen
            ? { padding: isDrawerOpen ? '16px 0' : isOverlay ? '0' : '24px 0' }
            : {}),
          margin: '0',
          height: isDrawerOpen ? '100%' : 'fit-content',
          boxSizing: 'border-box',
        }}
      >
        {/* The card a sheet (schedule, FAQs) covers: PaneSheet renders into it. */}
        <div
          data-pane-sheet-scope=""
          className={innerClassName}
          style={{
            display: 'flex',
            flexDirection: 'column',
            height:
              isDrawerOpen
                ? '100%'
                : fixedFormElementHeight || 'auto',
            ...fixedFormElementStyles,
            ...(isDrawerOpen && {
              margin: '0',
              padding: '10px',
              borderRadius: '0',
            }),
          }}
        >
          <div className="w-full">
            {/* The 52 title strip (Paper 2JU-0). Embeds, hosted pages and the
                overlay pane all name the step in it; the mobile drawer has its
                own strip, so it is skipped there. */}
            {!isDrawerOpen && headerTitle && (
              <FormTitleStrip id={headerId} onBack={onHeaderBack} backId={headerBackId}>
                {headerTitle}
              </FormTitleStrip>
            )}
          </div>
            <div
              className={`${!isDrawerOpen ? 'cs-form-card-body' : 'bg-white'} ${SURFACE_CONTENT_CLASS} flex flex-col flex-1 ${
                isDrawerOpen ? drawerBodyClassName : resolvedNonDrawerBodyClassName
              }`}
              id={bodyId}
              style={{
                height:
                isDrawerOpen
                  ? '100%'
                  : fixedFormElementHeight
                    ? 'auto'
                    : 'auto',
              minHeight: 0,
              ...(isDrawerOpen ? { padding: '0' } : {}),
              ...fixedFormBodyStyles,
            }}
            >
              {/* The step's content carries the slide; the card around it holds still. */}
              <div
                className={`${bodyClassName} ${pageTransitionClassName}`.trim()}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  flex: '1 1 auto',
                  minHeight: 0,
                  width: '100%',
                }}
            >
              {children}
            </div>
          </div>
        </div>
        {faq}
      </div>
    </div>
  );

  if (!usePageContainer) {
    return pageMarkup;
  }

  return (
    <div
      className="page-container"
      data-mode={isOverlay ? 'overlay' : isDrawerOpen ? 'drawer' : 'dynamic'}
      style={{
        minHeight: isDrawerOpen ? DRAWER_FORM_MIN_HEIGHT : 'fit-content',
        height: isDrawerOpen ? '100%' : 'auto',
      }}
    >
      {pageMarkup}
    </div>
  );
}

export default FormStartPageShell;
